import {
  Autocomplete,
  Box,
  FormControl,
  Grid,
  InputLabel,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import useAlert from '../../hooks/useAlert';
import useDebounce from '../../hooks/useDebounce';
import { ADDRESS_NOT_FOUND } from '../../error-messages';
import googleMapsService from '../../services/google-maps.service';
import { LocationOn } from '@mui/icons-material';
import parse from 'autosuggest-highlight/parse';
import { GooglePlacePrediction, GoogleMapResults, PropertyAddress } from '../../types/google-maps';

type LocationInputFieldProps = {
  addressInputValue: string;
  setAddressInputValue: (value: string) => void;
  onAddressSelect: (address: PropertyAddress) => void;
};

export default function LocationInputField(props: LocationInputFieldProps) {
  const alert = useAlert();
  const [googleAutocompleteOptions, setGoogleAutocompleteOptions] = useState<GoogleMapResults[]>(
    [],
  );
  const [autocompleteValue, setAutocompleteValue] = useState<GoogleMapResults | null>(
    props.addressInputValue ? { fullAddress: props.addressInputValue, googlePlaceId: '' } : null,
  );

  const queryGoogleAutocomplete = async () => {
    if (!props.addressInputValue) {
      setGoogleAutocompleteOptions(autocompleteValue ? [autocompleteValue] : []);
      return;
    }

    let addresses = [{ description: props.addressInputValue, place_id: '' }];

    try {
      const response = await googleMapsService.getPredictionsForGoogleAddress(
        props.addressInputValue,
      );

      if (response.data?.length > 0) {
        addresses = response.data.concat(addresses);
      }
    } catch (err: any) {
      console.error(err);
      alert.setAlert({ severity: 'error', message: err?.message ?? ADDRESS_NOT_FOUND });
    } finally {
      handleGoogleAutocompleteResults(addresses);
    }
  };

  const handleGoogleAutocompleteResults = (results: GooglePlacePrediction[]) => {
    const addresses: GoogleMapResults[] = [];

    for (const result of results) {
      addresses.push({
        googlePlaceId: result.place_id,
        fullAddress: result.description,
      });
    }

    setGoogleAutocompleteOptions(addresses);
  };

  const handleAddressSelection = async (address: GoogleMapResults) => {
    if (!address.googlePlaceId) {
      if (address.fullAddress) {
        setAutocompleteValue({ fullAddress: address.fullAddress, googlePlaceId: '' });
        props.onAddressSelect({
          address1: address.fullAddress,
        });
      }
      return;
    }

    const addressDetailsResponse = await googleMapsService.getPlaceDetailsForGooglePlaceId(
      address.googlePlaceId,
    );
    const addressComponents = addressDetailsResponse?.data?.address_components;

    if (addressComponents) {
      const streetNumber = addressComponents.find((component: any) =>
        component.types.includes('street_number'),
      )?.short_name;

      const route = addressComponents.find((component: any) => component.types.includes('route'))
        ?.short_name;

      const address1 = streetNumber && route ? `${streetNumber} ${route}` : '';

      const city =
        addressComponents.find((component: any) => component.types.includes('locality'))
          ?.short_name || '';

      const state =
        addressComponents.find((component: any) =>
          component.types.includes('administrative_area_level_1'),
        )?.short_name || '';

      const postalCode =
        addressComponents.find((component: any) => component.types.includes('postal_code'))
          ?.short_name || '';

      setAutocompleteValue({
        fullAddress: `${address1}, ${city}, ${state} ${postalCode}`,
        googlePlaceId: address.googlePlaceId,
      });

      props.onAddressSelect({
        address1,
        city,
        state,
        postalCode,
        googlePlaceId: address.googlePlaceId || '',
      });
    }
  };

  const debouncedAddressInputValue = useDebounce(props.addressInputValue, 300);

  useEffect(() => {
    queryGoogleAutocomplete();
  }, [debouncedAddressInputValue]);

  return (
    <Grid container>
      <Grid item sx={{ width: '100%' }}>
        <FormControl fullWidth>
          <Autocomplete
            options={googleAutocompleteOptions}
            filterOptions={x => x}
            size='small'
            getOptionLabel={option => option.fullAddress}
            isOptionEqualToValue={(option, value) => option.googlePlaceId === value?.googlePlaceId}
            sx={{ '& .MuiAutocomplete-endAdornment': { display: 'none' }, width: '100%' }}
            value={autocompleteValue}
            onChange={(event, value) => {
              if (value) handleAddressSelection(value as GoogleMapResults);
            }}
            onInputChange={(event, newInputValue) => props.setAddressInputValue(newInputValue)}
            renderInput={params => (
              <TextField {...params} variant='outlined' placeholder='800 Brazos St' fullWidth />
            )}
            renderOption={(props, option) => {
              const matches = [option.fullAddress] || [];

              const parts = parse(
                option.fullAddress,
                matches.map((match: any) => [match.offset, match.offset + match.length]),
              );

              return (
                <li {...props}>
                  <Grid container alignItems='center'>
                    <Grid item sx={{ display: 'flex', width: 44 }}>
                      <LocationOn sx={{ color: 'text.secondary' }} />
                    </Grid>
                    <Grid item sx={{ width: 'calc(100% - 44px)', wordWrap: 'break-word' }}>
                      {parts.map((part, index) => (
                        <Box
                          key={index}
                          component='span'
                          sx={{ fontWeight: part.highlight ? 'bold' : 'regular' }}
                        >
                          {part.text}
                        </Box>
                      ))}
                      <Typography variant='body2' color='text.primary'>
                        {option.fullAddress}
                      </Typography>
                    </Grid>
                  </Grid>
                </li>
              );
            }}
          />
        </FormControl>
      </Grid>
    </Grid>
  );
}
